/**
 * VR Engine - BabylonJS WebXR for Meta Quest 3/3S
 *
 * Using official BabylonJS WebXR hand tracking implementation
 * Based on: https://doc.babylonjs.com/features/featuresDeepDive/webXR/WebXRSelectedFeatures/WebXRHandTracking
 *
 * Features:
 * - Immersive VR mode (immersive-vr)
 * - Mixed Reality mode with passthrough (immersive-ar)
 * - Native hand tracking with 25 joints per hand
 * - Controller support with haptics
 * - Pinch-to-grab interactions
 * - Two-hand scale/rotate
 * - Teleportation
 * - Near interactions for UI
 */

export interface VREngineConfig {
  canvas: HTMLCanvasElement;
  onReady?: () => void;
  onSessionStart?: () => void;
  onSessionEnd?: () => void;
  onError?: (error: Error) => void;
  onModelGrabbed?: (hand: 'left' | 'right') => void;
  onModelReleased?: () => void;
  onScaleChanged?: (scale: number) => void;
}

export interface VRModelOptions {
  url: string;
  scale?: number;
  position?: { x: number; y: number; z: number };
}

export class VREngine {
  private config: VREngineConfig;
  private engine: BABYLON.Engine | null = null;
  private scene: BABYLON.Scene | null = null;
  private xrHelper: BABYLON.WebXRDefaultExperience | null = null;
  private model: BABYLON.TransformNode | null = null;
  private shadowGenerator: BABYLON.ShadowGenerator | null = null;
  private dirLight: BABYLON.DirectionalLight | null = null;
  private ground: BABYLON.Mesh | null = null;

  // Hand tracking
  private handTrackingFeature: BABYLON.WebXRHandTracking | null = null;
  private leftHand: BABYLON.WebXRHand | null = null;
  private rightHand: BABYLON.WebXRHand | null = null;

  // Grab state
  private isGrabbing = false;
  private grabbingHand: 'left' | 'right' | null = null;
  private grabOffset: BABYLON.Vector3 | null = null;

  // Two-hand interaction
  private isTwoHandGrab = false;
  private initialTwoHandDistance = 0;
  private initialScale = 1;
  private initialRotationY = 0;
  private initialTwoHandAngle = 0;

  // State
  private _isReady = false;
  private _isInVR = false;
  private _isInAR = false;
  private _vrSupported = false;
  private _arSupported = false;
  private _currentMode: 'vr' | 'ar' | null = null;

  // Environment meshes to hide in AR mode
  private environmentMeshes: BABYLON.Mesh[] = [];
  private originalClearColor: BABYLON.Color4 | null = null;

  // Joystick movement
  private cameraRig: BABYLON.TransformNode | null = null;
  private movementSpeed = 0.05;
  private rotationSpeed = 0.04;
  private leftStickX = 0;
  private leftStickY = 0;
  private rightStickX = 0;
  private rightStickY = 0;

  // Spatial UI - Wrist-mounted menu
  private vrUIPanel: BABYLON.TransformNode | null = null;
  private exitButton: BABYLON.Mesh | null = null;
  private exitButtonMaterial: BABYLON.StandardMaterial | null = null;
  private uiElements: BABYLON.Mesh[] = [];
  private isUIVisible = true;
  private uiAttachedToWrist = true;

  // Controller references for model placement
  private leftController: BABYLON.WebXRInputSource | null = null;
  private rightController: BABYLON.WebXRInputSource | null = null;

  // Placement indicator
  private placementIndicator: BABYLON.Mesh | null = null;
  private isPlacementMode = false;

  // Scaling state
  private rightTriggerHeld = false;
  private rightGripHeld = false;
  private scaleSpeed = 0.02;

  constructor(config: VREngineConfig) {
    this.config = config;
  }

  get isReady(): boolean {
    return this._isReady;
  }

  get isInVR(): boolean {
    return this._isInVR;
  }

  get vrSupported(): boolean {
    return this._vrSupported;
  }

  get arSupported(): boolean {
    return this._arSupported;
  }

  get isInAR(): boolean {
    return this._isInAR;
  }

  get currentMode(): 'vr' | 'ar' | null {
    return this._currentMode;
  }

  async init(): Promise<void> {
    try {
      await this.initBabylon();
      await this.checkVRSupport();
      if (this._vrSupported) {
        await this.initWebXR();
      }
      this._isReady = true;
      this.config.onReady?.();
    } catch (error) {
      console.error('[VR Engine] Init error:', error);
      this.config.onError?.(error as Error);
    }
  }

  private async checkVRSupport(): Promise<void> {
    if (navigator.xr) {
      // Check VR support
      this._vrSupported = await navigator.xr.isSessionSupported('immersive-vr');

      // Check AR/MR support (passthrough on Quest 3/3S)
      try {
        this._arSupported = await navigator.xr.isSessionSupported('immersive-ar');
      } catch {
        this._arSupported = false;
      }

      console.log('[VR Engine] VR supported:', this._vrSupported);
      console.log('[VR Engine] AR/MR supported:', this._arSupported);
    }
  }

  private async initBabylon(): Promise<void> {
    const { canvas } = this.config;

    // Use WebGL for VR (WebGPU has limited XR support)
    this.engine = new BABYLON.Engine(canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
      xrCompatible: true,
      powerPreference: 'high-performance',
    });

    this.scene = new BABYLON.Scene(this.engine);
    this.scene.clearColor = new BABYLON.Color4(0.05, 0.05, 0.1, 1);

    // Draco decoder for compressed models
    BABYLON.DracoCompression.Configuration = {
      decoder: {
        wasmUrl: 'https://cdn.babylonjs.com/draco_wasm_wrapper_gltf.js',
        wasmBinaryUrl: 'https://cdn.babylonjs.com/draco_decoder_gltf.wasm',
        fallbackUrl: 'https://cdn.babylonjs.com/draco_decoder_gltf.js',
      },
    };

    // Environment lighting
    this.scene.environmentTexture = BABYLON.CubeTexture.CreateFromPrefilteredData(
      'https://playground.babylonjs.com/textures/environment.env',
      this.scene
    );
    this.scene.environmentIntensity = 1.0;

    // Image processing for better visuals
    this.scene.imageProcessingConfiguration.exposure = 1.2;
    this.scene.imageProcessingConfiguration.contrast = 1.1;
    this.scene.imageProcessingConfiguration.toneMappingEnabled = true;
    this.scene.imageProcessingConfiguration.toneMappingType =
      BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES;

    // Camera (for non-VR preview)
    const camera = new BABYLON.ArcRotateCamera(
      'camera',
      -Math.PI / 2,
      Math.PI / 2.5,
      5,
      new BABYLON.Vector3(0, 1, 0),
      this.scene
    );
    camera.minZ = 0.01;
    camera.maxZ = 1000;
    camera.wheelPrecision = 50;
    camera.attachControl(canvas, true);

    // Lights
    const hemiLight = new BABYLON.HemisphericLight('hemi', new BABYLON.Vector3(0, 1, 0), this.scene);
    hemiLight.intensity = 0.6;
    hemiLight.groundColor = new BABYLON.Color3(0.2, 0.2, 0.3);

    this.dirLight = new BABYLON.DirectionalLight('dir', new BABYLON.Vector3(-0.5, -1, 0.5), this.scene);
    this.dirLight.position = new BABYLON.Vector3(5, 10, 5);
    this.dirLight.intensity = 1.0;

    // Shadows
    this.shadowGenerator = new BABYLON.ShadowGenerator(1024, this.dirLight);
    this.shadowGenerator.useBlurExponentialShadowMap = true;
    this.shadowGenerator.blurKernel = 32;
    this.shadowGenerator.darkness = 0.3;

    // Create VR environment
    this.createEnvironment();

    // Render loop
    this.engine.runRenderLoop(() => {
      this.update();
      this.scene?.render();
    });

    window.addEventListener('resize', () => this.engine?.resize());
  }

  private createEnvironment(): void {
    if (!this.scene) return;

    // Store original clear color for AR mode toggle
    this.originalClearColor = this.scene.clearColor.clone();

    // Ground with grid
    this.ground = BABYLON.MeshBuilder.CreateGround('ground', { width: 30, height: 30 }, this.scene);
    const groundMat = new BABYLON.GridMaterial('groundMat', this.scene);
    groundMat.majorUnitFrequency = 5;
    groundMat.minorUnitVisibility = 0.4;
    groundMat.gridRatio = 1;
    groundMat.mainColor = new BABYLON.Color3(0.1, 0.1, 0.2);
    groundMat.lineColor = new BABYLON.Color3(0.3, 0.3, 0.5);
    groundMat.opacity = 0.9;
    groundMat.backFaceCulling = false;
    this.ground.material = groundMat;
    this.ground.receiveShadows = true;
    this.environmentMeshes.push(this.ground);

    // Pedestal for model
    const pedestal = BABYLON.MeshBuilder.CreateCylinder('pedestal', {
      height: 0.05,
      diameter: 2,
      tessellation: 48,
    }, this.scene);
    pedestal.position.y = 0.025;
    const pedestalMat = new BABYLON.PBRMaterial('pedestalMat', this.scene);
    pedestalMat.albedoColor = new BABYLON.Color3(0.1, 0.1, 0.15);
    pedestalMat.metallic = 0.9;
    pedestalMat.roughness = 0.2;
    pedestal.material = pedestalMat;
    this.environmentMeshes.push(pedestal);

    // Glow ring around pedestal
    const ring = BABYLON.MeshBuilder.CreateTorus('ring', {
      diameter: 2.1,
      thickness: 0.02,
      tessellation: 64,
    }, this.scene);
    ring.position.y = 0.06;
    const ringMat = new BABYLON.StandardMaterial('ringMat', this.scene);
    ringMat.emissiveColor = new BABYLON.Color3(0.2, 0.5, 1);
    ringMat.disableLighting = true;
    ring.material = ringMat;
    this.environmentMeshes.push(ring);

    // Ambient particles
    this.createParticles();
  }

  private createParticles(): void {
    if (!this.scene) return;

    const ps = new BABYLON.ParticleSystem('particles', 300, this.scene);
    ps.particleTexture = new BABYLON.Texture(
      'https://playground.babylonjs.com/textures/flare.png',
      this.scene
    );

    ps.emitter = new BABYLON.Vector3(0, 3, 0);
    ps.minEmitBox = new BABYLON.Vector3(-8, -2, -8);
    ps.maxEmitBox = new BABYLON.Vector3(8, 4, 8);

    ps.color1 = new BABYLON.Color4(0.3, 0.5, 1, 0.15);
    ps.color2 = new BABYLON.Color4(0.5, 0.3, 1, 0.1);
    ps.colorDead = new BABYLON.Color4(0, 0, 0.2, 0);

    ps.minSize = 0.03;
    ps.maxSize = 0.1;
    ps.minLifeTime = 4;
    ps.maxLifeTime = 8;
    ps.emitRate = 15;
    ps.gravity = new BABYLON.Vector3(0, -0.02, 0);
    ps.direction1 = new BABYLON.Vector3(-0.3, 0.5, -0.3);
    ps.direction2 = new BABYLON.Vector3(0.3, 1, 0.3);
    ps.minEmitPower = 0.05;
    ps.maxEmitPower = 0.15;
    ps.blendMode = BABYLON.ParticleSystem.BLENDMODE_ADD;

    ps.start();
  }

  private async initWebXR(): Promise<void> {
    if (!this.scene || !this.ground) return;

    // Create XR experience with hand tracking
    // IMPORTANT: Disable default enter button - we use our custom button
    this.xrHelper = await this.scene.createDefaultXRExperienceAsync({
      uiOptions: {
        sessionMode: 'immersive-vr',
        referenceSpaceType: 'local-floor',
      },
      inputOptions: {
        doNotLoadControllerMeshes: false,
      },
      optionalFeatures: true,
      disableDefaultUI: true, // Hide BabylonJS default VR goggles button
    });

    // Session state handling
    this.xrHelper.baseExperience.onStateChangedObservable.add((state) => {
      if (state === BABYLON.WebXRState.IN_XR) {
        if (this._currentMode === 'ar') {
          this._isInAR = true;
          this._isInVR = false;
        } else {
          this._isInVR = true;
          this._isInAR = false;
        }
        this.config.onSessionStart?.();
      } else if (state === BABYLON.WebXRState.NOT_IN_XR) {
        this._isInVR = false;
        this._isInAR = false;
        this._currentMode = null;
        this.config.onSessionEnd?.();
      }
    });

    // Teleportation (disabled - we use custom movement)
    // this.setupTeleportation();

    // Enable pointer selection for UI interaction
    this.setupPointerSelection();

    // Hand tracking (official BabylonJS implementation)
    this.setupHandTracking();

    // Controller interactions
    this.setupControllers();
  }

  private setupPointerSelection(): void {
    if (!this.xrHelper) return;

    try {
      // Enable controller pointer selection for clicking on UI
      const pointerSelection = this.xrHelper.baseExperience.featuresManager.enableFeature(
        BABYLON.WebXRFeatureName.POINTER_SELECTION,
        'stable',
        {
          xrInput: this.xrHelper.input,
          enablePointerSelectionOnAllControllers: true,
          preferredHandedness: 'right', // Use right controller for UI interaction
          disableSwitchOnClick: false,
          gazeCamera: this.xrHelper.baseExperience.camera,
        }
      );

      console.log('[VR] Pointer selection enabled');
    } catch (e) {
      console.warn('[VR] Pointer selection not available:', e);
    }
  }

  private setupTeleportation(): void {
    if (!this.xrHelper || !this.ground) return;

    try {
      this.xrHelper.baseExperience.featuresManager.enableFeature(
        BABYLON.WebXRFeatureName.TELEPORTATION,
        'stable',
        {
          xrInput: this.xrHelper.input,
          floorMeshes: [this.ground],
          defaultTargetMeshOptions: {
            teleportationFillColor: '#3366ff',
            teleportationBorderColor: '#6699ff',
          },
          timeToTeleport: 1500,
          useMainComponentOnly: true,
        }
      );
    } catch (e) {
      console.warn('[VR] Teleportation not available:', e);
    }
  }

  private setupHandTracking(): void {
    if (!this.xrHelper) return;

    try {
      // Enable hand tracking with BabylonJS default hand meshes
      this.handTrackingFeature = this.xrHelper.baseExperience.featuresManager.enableFeature(
        BABYLON.WebXRFeatureName.HAND_TRACKING,
        'latest',
        {
          xrInput: this.xrHelper.input,
          jointMeshes: {
            disableDefaultHandMesh: false, // Use BabylonJS built-in hand meshes
            enablePhysics: false,
          },
        }
      ) as BABYLON.WebXRHandTracking;

      // Hand added event
      this.handTrackingFeature.onHandAddedObservable.add((hand) => {
        const handedness = hand.xrController.inputSource.handedness;
        console.log(`[VR] Hand added: ${handedness}`);

        if (handedness === 'left') {
          this.leftHand = hand;
        } else if (handedness === 'right') {
          this.rightHand = hand;
        }
      });

      // Hand removed event
      this.handTrackingFeature.onHandRemovedObservable.add((hand) => {
        const handedness = hand.xrController.inputSource.handedness;
        if (handedness === 'left') {
          this.leftHand = null;
        } else if (handedness === 'right') {
          this.rightHand = null;
        }
      });

    } catch (e) {
      console.warn('[VR] Hand tracking not available:', e);
    }
  }

  private setupControllers(): void {
    if (!this.xrHelper) return;

    // Create placement indicator (circle on the ground)
    this.createPlacementIndicator();

    this.xrHelper.input.onControllerAddedObservable.add((controller) => {
      const handedness = controller.inputSource.handedness;
      console.log(`[VR] Controller added: ${handedness}`);

      // Store controller references
      if (handedness === 'left') {
        this.leftController = controller;
      } else if (handedness === 'right') {
        this.rightController = controller;
      }

      controller.onMotionControllerInitObservable.add((mc) => {
        try {
          // ==========================================
          // LEFT CONTROLLER - PLAYER Control Only
          // ==========================================
          if (handedness === 'left') {
            // Left Stick: Move yourself
            const thumbstick = mc.getComponent('xr-standard-thumbstick');
            if (thumbstick) {
              thumbstick.onAxisValueChangedObservable.add((axes) => {
                this.leftStickX = Math.abs(axes.x) > 0.15 ? axes.x : 0;
                this.leftStickY = Math.abs(axes.y) > 0.15 ? axes.y : 0;
              });
            }

            // Left Trigger: Teleport PLAYER where pointing
            const trigger = mc.getComponent('xr-standard-trigger');
            if (trigger) {
              trigger.onButtonStateChangedObservable.add((comp) => {
                try {
                  if (comp.pressed) {
                    this.teleportPlayerToPointer(controller);
                  }
                } catch (e) {
                  console.warn('[VR] Left trigger error:', e);
                }
              });
            }

            // Left Grip: Toggle UI visibility
            const grip = mc.getComponent('xr-standard-squeeze');
            if (grip) {
              grip.onButtonStateChangedObservable.add((comp) => {
                if (comp.pressed) {
                  this.toggleUIVisibility();
                }
              });
            }

            // X Button: Toggle UI attachment
            const xButton = mc.getComponent('x-button');
            if (xButton) {
              xButton.onButtonStateChangedObservable.add((comp) => {
                if (comp.pressed) {
                  this.toggleUIAttachment();
                }
              });
            }

            // Y Button: Reset player position to origin
            const yButton = mc.getComponent('y-button');
            if (yButton) {
              yButton.onButtonStateChangedObservable.add((comp) => {
                if (comp.pressed) {
                  this.resetPlayerPosition();
                }
              });
            }

            // Menu button: Exit VR/AR
            const menuButton = mc.getComponent('menu');
            if (menuButton) {
              menuButton.onButtonStateChangedObservable.add((comp) => {
                if (comp.pressed) {
                  this.exitVR();
                }
              });
            }
          }

          // ==========================================
          // RIGHT CONTROLLER - MODEL Control Only
          // ==========================================
          if (handedness === 'right') {
            // Right Stick: Rotate (X) and Move model forward/back (Y)
            const thumbstick = mc.getComponent('xr-standard-thumbstick');
            if (thumbstick) {
              thumbstick.onAxisValueChangedObservable.add((axes) => {
                this.rightStickX = Math.abs(axes.x) > 0.15 ? axes.x : 0;
                this.rightStickY = Math.abs(axes.y) > 0.15 ? axes.y : 0;
              });
            }

            // Right Trigger: Teleport MODEL where pointing
            const trigger = mc.getComponent('xr-standard-trigger');
            if (trigger) {
              trigger.onButtonStateChangedObservable.add((comp) => {
                try {
                  if (comp.pressed) {
                    this.placeModelAtPointer(controller);
                  }
                  this.isPlacementMode = comp.value > 0.1;
                } catch (e) {
                  console.warn('[VR] Right trigger error:', e);
                }
              });
            }

            // Right Grip: Reset model to center
            const grip = mc.getComponent('xr-standard-squeeze');
            if (grip) {
              grip.onButtonStateChangedObservable.add((comp) => {
                if (comp.pressed) {
                  this.resetModelToGround();
                }
              });
            }

            // A Button: Scale UP
            const aButton = mc.getComponent('a-button');
            if (aButton) {
              aButton.onButtonStateChangedObservable.add((comp) => {
                if (comp.pressed && this.model) {
                  const newScale = Math.min(15, this.model.scaling.x * 1.25);
                  this.model.scaling.setAll(newScale);
                  this.config.onScaleChanged?.(newScale);
                }
              });
            }

            // B Button: Scale DOWN
            const bButton = mc.getComponent('b-button');
            if (bButton) {
              bButton.onButtonStateChangedObservable.add((comp) => {
                if (comp.pressed && this.model) {
                  const newScale = Math.max(0.02, this.model.scaling.x * 0.75);
                  this.model.scaling.setAll(newScale);
                  this.config.onScaleChanged?.(newScale);
                }
              });
            }
          }
        } catch (e) {
          console.warn('[VR] Controller setup error:', e);
        }
      });
    });

    // Handle controller removal
    this.xrHelper.input.onControllerRemovedObservable.add((controller) => {
      const handedness = controller.inputSource.handedness;
      if (handedness === 'left') {
        this.leftController = null;
      } else if (handedness === 'right') {
        this.rightController = null;
      }
    });
  }

  /**
   * Create a placement indicator that shows where the model will be placed
   */
  private createPlacementIndicator(): void {
    if (!this.scene) return;

    // Create a ring indicator
    this.placementIndicator = BABYLON.MeshBuilder.CreateTorus('placementIndicator', {
      diameter: 0.8,
      thickness: 0.03,
      tessellation: 32,
    }, this.scene);

    const mat = new BABYLON.StandardMaterial('placementMat', this.scene);
    mat.emissiveColor = new BABYLON.Color3(0, 0.8, 1);
    mat.disableLighting = true;
    mat.alpha = 0.7;
    this.placementIndicator.material = mat;

    // Create inner disc
    const disc = BABYLON.MeshBuilder.CreateDisc('placementDisc', {
      radius: 0.35,
      tessellation: 32,
    }, this.scene);
    disc.parent = this.placementIndicator;
    disc.rotation.x = Math.PI / 2;

    const discMat = new BABYLON.StandardMaterial('discMat', this.scene);
    discMat.emissiveColor = new BABYLON.Color3(0, 0.5, 0.8);
    discMat.disableLighting = true;
    discMat.alpha = 0.3;
    disc.material = discMat;

    this.placementIndicator.isVisible = false;
    this.placementIndicator.position.y = 0.02; // Just above ground
  }

  /**
   * Teleport PLAYER to where left controller is pointing
   */
  private teleportPlayerToPointer(controller: BABYLON.WebXRInputSource): void {
    if (!this.scene || !this.ground || !this.xrHelper?.baseExperience?.camera) return;

    try {
      const ray = controller.getWorldPointerRayToRef(new BABYLON.Ray(BABYLON.Vector3.Zero(), BABYLON.Vector3.Forward()));
      const pickResult = this.scene.pickWithRay(ray, (mesh) => mesh === this.ground);

      if (pickResult?.hit && pickResult.pickedPoint) {
        const camera = this.xrHelper.baseExperience.camera;
        // Move camera to picked point (keep Y at current height)
        camera.position.x = pickResult.pickedPoint.x;
        camera.position.z = pickResult.pickedPoint.z;
        console.log('[VR] Player teleported to:', pickResult.pickedPoint);
      }
    } catch (e) {
      console.warn('[VR] Teleport player error:', e);
    }
  }

  /**
   * Place MODEL at the position where right controller is pointing
   */
  private placeModelAtPointer(controller: BABYLON.WebXRInputSource): void {
    if (!this.model || !this.scene || !this.ground) return;

    try {
      const ray = controller.getWorldPointerRayToRef(new BABYLON.Ray(BABYLON.Vector3.Zero(), BABYLON.Vector3.Forward()));
      const pickResult = this.scene.pickWithRay(ray, (mesh) => mesh === this.ground);

      if (pickResult?.hit && pickResult.pickedPoint) {
        // Move model to picked point
        this.model.position.x = pickResult.pickedPoint.x;
        this.model.position.z = pickResult.pickedPoint.z;
        // Re-ground the model
        this.groundModel();
        console.log('[VR] Model placed at:', pickResult.pickedPoint);
      }
    } catch (e) {
      console.warn('[VR] Place model error:', e);
    }
  }

  /**
   * Toggle UI visibility
   */
  private toggleUIVisibility(): void {
    if (!this.vrUIPanel) return;
    this.isUIVisible = !this.isUIVisible;
    this.vrUIPanel.setEnabled(this.isUIVisible);
    console.log('[VR] UI visible:', this.isUIVisible);
  }

  /**
   * Reset player to starting position
   */
  private resetPlayerPosition(): void {
    if (!this.xrHelper?.baseExperience?.camera) return;
    const camera = this.xrHelper.baseExperience.camera;
    camera.position.x = 0;
    camera.position.z = 0;
    console.log('[VR] Player reset to origin');
  }

  /**
   * Update placement indicator position (follows RIGHT controller for model placement)
   */
  private updatePlacementIndicator(): void {
    if (!this.placementIndicator || !this.rightController || !this.scene || !this.ground) return;

    // Show indicator when right trigger is partially pressed (placement mode)
    if (this.isPlacementMode) {
      try {
        const ray = this.rightController.getWorldPointerRayToRef(
          new BABYLON.Ray(BABYLON.Vector3.Zero(), BABYLON.Vector3.Forward())
        );
        const pickResult = this.scene.pickWithRay(ray, (mesh) => mesh === this.ground);

        if (pickResult?.hit && pickResult.pickedPoint) {
          this.placementIndicator.isVisible = true;
          this.placementIndicator.position.x = pickResult.pickedPoint.x;
          this.placementIndicator.position.z = pickResult.pickedPoint.z;

          // Pulse animation
          const time = performance.now() / 1000;
          const scale = 1 + Math.sin(time * 4) * 0.1;
          this.placementIndicator.scaling.setAll(scale);
        } else {
          this.placementIndicator.isVisible = false;
        }
      } catch {
        this.placementIndicator.isVisible = false;
      }
    } else {
      this.placementIndicator.isVisible = false;
    }
  }

  private resetModelToGround(): void {
    if (!this.model) return;

    // Reset rotation and scale
    this.model.rotation = new BABYLON.Vector3(0, 0, 0);

    // Position model on ground in front of camera
    const camera = this.xrHelper?.baseExperience.camera;
    if (camera) {
      const forward = camera.getForwardRay().direction;
      const targetPos = camera.position.add(forward.scale(2));
      this.model.position.x = targetPos.x;
      this.model.position.z = targetPos.z;
    } else {
      this.model.position.x = 0;
      this.model.position.z = 2;
    }

    // Ground the model
    this.groundModel();
  }

  private update(): void {
    // Update when in XR session
    if (this._isInVR || this._isInAR) {
      this.updatePlayerMovement();
      this.updateModelManipulation();
      this.updatePlacementIndicator();
      this.updateSpatialUI();
    }

    if ((!this._isInVR && !this._isInAR) || !this.model) return;

    // Hand tracking update
    this.updateHandInteraction();
  }

  /**
   * Handle player movement with left stick
   */
  private updatePlayerMovement(): void {
    if (!this.xrHelper?.baseExperience?.camera) return;

    // Movement with left stick only
    if (Math.abs(this.leftStickX) < 0.1 && Math.abs(this.leftStickY) < 0.1) return;

    try {
      const camera = this.xrHelper.baseExperience.camera;

      // Get camera forward direction (flattened to XZ plane)
      const forward = camera.getDirection(BABYLON.Vector3.Forward());
      forward.y = 0;
      if (forward.length() > 0.001) forward.normalize();

      // Get camera right direction
      const right = camera.getDirection(BABYLON.Vector3.Right());
      right.y = 0;
      if (right.length() > 0.001) right.normalize();

      // Calculate movement vector
      const movement = new BABYLON.Vector3(0, 0, 0);
      movement.addInPlace(forward.scale(-this.leftStickY * this.movementSpeed));
      movement.addInPlace(right.scale(this.leftStickX * this.movementSpeed));

      // Apply movement to camera position
      camera.position.addInPlace(movement);
    } catch (e) {
      console.warn('[VR] Movement error:', e);
    }
  }

  /**
   * Handle model manipulation with right stick and triggers
   */
  private updateModelManipulation(): void {
    if (!this.model) return;

    try {
      // Right stick X: Rotate model around Y axis
      if (Math.abs(this.rightStickX) > 0.1) {
        this.model.rotation.y += this.rightStickX * this.rotationSpeed * 1.5;
      }

      // Right stick Y: Move model forward/backward relative to camera
      if (Math.abs(this.rightStickY) > 0.1 && this.xrHelper?.baseExperience?.camera) {
        const camera = this.xrHelper.baseExperience.camera;
        const forward = camera.getDirection(BABYLON.Vector3.Forward());
        forward.y = 0;
        if (forward.length() > 0.001) forward.normalize();

        // Move model in camera's forward direction
        this.model.position.addInPlace(forward.scale(-this.rightStickY * 0.03));
      }

      // Right Trigger held: Scale UP continuously
      if (this.rightTriggerHeld) {
        const newScale = Math.min(15, this.model.scaling.x * (1 + this.scaleSpeed));
        this.model.scaling.setAll(newScale);
      }

      // Right Grip held: Scale DOWN continuously
      if (this.rightGripHeld) {
        const newScale = Math.max(0.02, this.model.scaling.x * (1 - this.scaleSpeed));
        this.model.scaling.setAll(newScale);
      }
    } catch (e) {
      console.warn('[VR] Model manipulation error:', e);
    }
  }

  private updateSpatialUI(): void {
    if (!this.vrUIPanel || !this.xrHelper?.baseExperience?.camera) return;

    try {
      const camera = this.xrHelper.baseExperience.camera;

      if (this.uiAttachedToWrist && this.leftController) {
        // Attach to left wrist - position relative to left controller
        const grip = this.leftController.grip;
        if (grip) {
          // Position above the wrist, rotated to face user
          const gripPos = grip.position.clone();
          const gripRot = grip.rotationQuaternion;

          // Offset above wrist
          this.vrUIPanel.position = gripPos.add(new BABYLON.Vector3(0, 0.15, 0));

          // Make UI face the camera
          const dirToCamera = camera.position.subtract(this.vrUIPanel.position);
          dirToCamera.y = 0;
          if (dirToCamera.length() > 0.01) {
            dirToCamera.normalize();
            const angle = Math.atan2(dirToCamera.x, dirToCamera.z);
            this.vrUIPanel.rotation = new BABYLON.Vector3(0, angle, 0);
          }
        }
      } else {
        // Free-floating mode - stays at last position but always faces user
        const dirToCamera = camera.position.subtract(this.vrUIPanel.position);
        dirToCamera.y = 0;
        if (dirToCamera.length() > 0.01) {
          dirToCamera.normalize();
          const angle = Math.atan2(dirToCamera.x, dirToCamera.z);
          this.vrUIPanel.rotation = new BABYLON.Vector3(0, angle, 0);
        }
      }
    } catch (e) {
      // Ignore errors during UI update
    }
  }

  private updateHandInteraction(): void {
    if (!this.leftHand && !this.rightHand) return;

    const leftPinch = this.isPinching(this.leftHand);
    const rightPinch = this.isPinching(this.rightHand);

    // Two-hand interaction (scale + rotate)
    if (leftPinch && rightPinch && this.leftHand && this.rightHand) {
      this.handleTwoHandInteraction();
      return;
    } else if (this.isTwoHandGrab) {
      this.isTwoHandGrab = false;
    }

    // Single hand grab
    if (leftPinch && !this.isGrabbing && this.leftHand) {
      const pos = this.getHandPosition(this.leftHand);
      this.tryGrab('left', pos);
    } else if (rightPinch && !this.isGrabbing && this.rightHand) {
      const pos = this.getHandPosition(this.rightHand);
      this.tryGrab('right', pos);
    } else if (!leftPinch && this.grabbingHand === 'left') {
      this.releaseGrab();
    } else if (!rightPinch && this.grabbingHand === 'right') {
      this.releaseGrab();
    }

    // Update grab position
    if (this.isGrabbing && this.grabOffset && this.model) {
      const hand = this.grabbingHand === 'left' ? this.leftHand : this.rightHand;
      if (hand) {
        const handPos = this.getHandPosition(hand);
        this.model.position = handPos.subtract(this.grabOffset);
        // Keep above ground
        if (this.model.position.y < 0) {
          this.model.position.y = 0;
        }
      }
    }
  }

  private isPinching(hand: BABYLON.WebXRHand | null): boolean {
    if (!hand) return false;

    try {
      const thumbTip = hand.getJointMesh(BABYLON.WebXRHandJoint.THUMB_TIP);
      const indexTip = hand.getJointMesh(BABYLON.WebXRHandJoint.INDEX_FINGER_TIP);

      if (!thumbTip || !indexTip) return false;

      const distance = BABYLON.Vector3.Distance(
        thumbTip.absolutePosition,
        indexTip.absolutePosition
      );

      return distance < 0.025; // 2.5cm pinch threshold
    } catch {
      return false;
    }
  }

  private getHandPosition(hand: BABYLON.WebXRHand): BABYLON.Vector3 {
    try {
      // Use index finger tip for more precise grabbing
      const indexTip = hand.getJointMesh(BABYLON.WebXRHandJoint.INDEX_FINGER_TIP);
      if (indexTip) {
        return indexTip.absolutePosition.clone();
      }
      // Fallback to wrist
      const wrist = hand.getJointMesh(BABYLON.WebXRHandJoint.WRIST);
      if (wrist) {
        return wrist.absolutePosition.clone();
      }
    } catch {
      // Fallback
    }
    return new BABYLON.Vector3(0, 1, 0);
  }

  private tryGrab(hand: 'left' | 'right', position: BABYLON.Vector3): void {
    if (!this.model || this.isGrabbing) return;

    // Check distance to model
    const modelPos = this.model.getAbsolutePosition();
    const distance = BABYLON.Vector3.Distance(position, modelPos);

    // Get model bounding box for better grab detection
    const boundingInfo = this.model.getHierarchyBoundingVectors(true);
    const modelSize = boundingInfo.max.subtract(boundingInfo.min);
    const grabRadius = Math.max(modelSize.x, modelSize.y, modelSize.z) / 2 + 0.3;

    if (distance <= grabRadius) {
      this.isGrabbing = true;
      this.grabbingHand = hand;
      this.grabOffset = position.subtract(modelPos);
      this.config.onModelGrabbed?.(hand);

      // Visual feedback
      this.highlightModel(true);
    }
  }

  private releaseGrab(): void {
    if (!this.isGrabbing) return;

    this.isGrabbing = false;
    this.grabbingHand = null;
    this.grabOffset = null;
    this.config.onModelReleased?.();

    // Remove highlight
    this.highlightModel(false);
  }

  private highlightModel(highlight: boolean): void {
    if (!this.model) return;

    this.model.getChildMeshes().forEach((mesh) => {
      if (mesh.material instanceof BABYLON.PBRMaterial) {
        mesh.material.emissiveColor = highlight
          ? new BABYLON.Color3(0.1, 0.15, 0.25)
          : new BABYLON.Color3(0, 0, 0);
      }
    });
  }

  private handleTwoHandInteraction(): void {
    if (!this.leftHand || !this.rightHand || !this.model) return;

    const leftPos = this.getHandPosition(this.leftHand);
    const rightPos = this.getHandPosition(this.rightHand);
    const currentDist = BABYLON.Vector3.Distance(leftPos, rightPos);

    if (!this.isTwoHandGrab) {
      // Start two-hand interaction
      this.isTwoHandGrab = true;
      this.initialTwoHandDistance = currentDist;
      this.initialScale = this.model.scaling.x;
      this.initialRotationY = this.model.rotation.y;

      const dx = rightPos.x - leftPos.x;
      const dz = rightPos.z - leftPos.z;
      this.initialTwoHandAngle = Math.atan2(dz, dx);
      return;
    }

    // Scale based on hand distance
    const scaleRatio = currentDist / this.initialTwoHandDistance;
    let newScale = this.initialScale * scaleRatio;
    newScale = Math.max(0.1, Math.min(5, newScale));
    this.model.scaling.setAll(newScale);
    this.config.onScaleChanged?.(newScale);

    // Rotate based on hand angle
    const dx = rightPos.x - leftPos.x;
    const dz = rightPos.z - leftPos.z;
    const currentAngle = Math.atan2(dz, dx);
    const deltaAngle = currentAngle - this.initialTwoHandAngle;
    this.model.rotation.y = this.initialRotationY + deltaAngle;

    // Position at midpoint
    const midpoint = BABYLON.Vector3.Center(leftPos, rightPos);
    this.model.position.x = midpoint.x;
    this.model.position.z = midpoint.z;
  }

  async loadModel(options: VRModelOptions): Promise<void> {
    if (!this.scene) return;

    const { url, scale = 1, position = { x: 0, y: 1, z: 2 } } = options;

    // Remove existing model
    if (this.model) {
      this.model.dispose();
      this.model = null;
    }

    try {
      const result = await BABYLON.SceneLoader.ImportMeshAsync('', url, '', this.scene);

      this.model = result.meshes[0] as BABYLON.TransformNode;

      // Setup shadows and materials
      result.meshes.forEach((mesh) => {
        if (mesh.name !== '__root__' && this.shadowGenerator) {
          this.shadowGenerator.addShadowCaster(mesh);
          mesh.receiveShadows = true;
        }

        // Enhance PBR
        if (mesh.material instanceof BABYLON.PBRMaterial) {
          mesh.material.useRadianceOcclusion = true;
          mesh.material.useHorizonOcclusion = true;
          mesh.material.environmentIntensity = 1.2;
        }
      });

      // Auto-scale based on bounding box - aim for ~1.5m tall for comfortable viewing
      this.model.computeWorldMatrix(true);
      const boundingInfo = this.model.getHierarchyBoundingVectors(true);
      const size = boundingInfo.max.subtract(boundingInfo.min);
      const maxDim = Math.max(size.x, size.y, size.z);

      // Target size: make model about 1.5 meters for VR viewing
      // This ensures it's a comfortable size at arm's length
      const targetSize = 1.5 * scale;
      const autoScale = maxDim > 0 ? targetSize / maxDim : scale;
      this.model.scaling.setAll(autoScale);

      // Initial position - place in front of user
      this.model.position = new BABYLON.Vector3(position.x, position.y, position.z);

      // Ground the model (calculate bounding box and offset so bottom touches ground)
      this.groundModel();

      // Spawn animation
      this.playSpawnAnimation(autoScale);

      // Create spatial UI when model loads
      this.createSpatialUI();

    } catch (error) {
      console.error('[VR] Failed to load model:', error);
      this.config.onError?.(error as Error);
    }
  }

  /**
   * Position the model so its bottom touches the ground (y=0)
   */
  private groundModel(): void {
    if (!this.model) return;

    // Recalculate bounding box after scaling
    this.model.computeWorldMatrix(true);
    const boundingInfo = this.model.getHierarchyBoundingVectors(true);

    // Get the minimum Y value of the model
    const minY = boundingInfo.min.y;

    // Offset the model so its bottom is at y=0 (ground level)
    // We need to account for the current model position
    const currentY = this.model.position.y;
    const offset = minY - currentY;

    // Set the new Y position so the model sits on the ground
    this.model.position.y = -offset;
  }

  /**
   * Create AAA wrist-mounted UI panel
   * Attached to left controller for easy access
   */
  private createSpatialUI(): void {
    if (!this.scene) return;

    // Dispose old UI
    this.disposeUI();

    // Create UI container
    this.vrUIPanel = new BABYLON.TransformNode('vrUIContainer', this.scene);
    this.vrUIPanel.position = new BABYLON.Vector3(-0.5, 1.2, 1);

    // ============================================
    // MAIN PANEL - Glassmorphism style
    // ============================================
    const panelWidth = 0.4;
    const panelHeight = 0.28;

    // Background panel with rounded corners effect
    const bgPanel = BABYLON.MeshBuilder.CreatePlane('bgPanel', {
      width: panelWidth,
      height: panelHeight,
      sideOrientation: BABYLON.Mesh.DOUBLESIDE,
    }, this.scene);
    bgPanel.parent = this.vrUIPanel;

    const bgMat = new BABYLON.StandardMaterial('bgMat', this.scene);
    bgMat.diffuseColor = new BABYLON.Color3(0.02, 0.02, 0.06);
    bgMat.emissiveColor = new BABYLON.Color3(0.01, 0.02, 0.04);
    bgMat.specularColor = new BABYLON.Color3(0.05, 0.05, 0.1);
    bgMat.alpha = 0.85;
    bgPanel.material = bgMat;
    this.uiElements.push(bgPanel);

    // Glowing border
    const border = BABYLON.MeshBuilder.CreatePlane('border', {
      width: panelWidth + 0.01,
      height: panelHeight + 0.01,
      sideOrientation: BABYLON.Mesh.DOUBLESIDE,
    }, this.scene);
    border.parent = this.vrUIPanel;
    border.position.z = 0.002;

    const borderMat = new BABYLON.StandardMaterial('borderMat', this.scene);
    borderMat.emissiveColor = new BABYLON.Color3(0, 0.6, 1);
    borderMat.alpha = 0.3;
    border.material = borderMat;
    this.uiElements.push(border);

    // ============================================
    // HEADER
    // ============================================
    const header = this.createUIText('VisionAR', {
      width: 0.35,
      height: 0.045,
      fontSize: 28,
      fontWeight: 'bold',
      color: '#00d4ff',
      bgColor: 'transparent',
      y: 0.10,
    });
    if (header) {
      header.parent = this.vrUIPanel;
      this.uiElements.push(header);
    }

    // ============================================
    // CONTROL HINTS (compact)
    // ============================================
    const leftHint = this.createUIText('◀ LEFT = PLAYER', {
      width: 0.16,
      height: 0.03,
      fontSize: 15,
      fontWeight: 'bold',
      color: '#88ff88',
      bgColor: 'rgba(0,80,0,0.4)',
      x: -0.1,
      y: 0.055,
    });
    if (leftHint) {
      leftHint.parent = this.vrUIPanel;
      this.uiElements.push(leftHint);
    }

    const rightHint = this.createUIText('MODEL = RIGHT ▶', {
      width: 0.16,
      height: 0.03,
      fontSize: 15,
      fontWeight: 'bold',
      color: '#ff88ff',
      bgColor: 'rgba(80,0,80,0.4)',
      x: 0.1,
      y: 0.055,
    });
    if (rightHint) {
      rightHint.parent = this.vrUIPanel;
      this.uiElements.push(rightHint);
    }

    // Control details
    const leftDetails = this.createUIText('Stick:Move | Trigger:Teleport', {
      width: 0.18,
      height: 0.022,
      fontSize: 11,
      color: '#aaffaa',
      bgColor: 'transparent',
      x: -0.095,
      y: 0.025,
    });
    if (leftDetails) {
      leftDetails.parent = this.vrUIPanel;
      this.uiElements.push(leftDetails);
    }

    const rightDetails = this.createUIText('Stick:Rotate | Trigger:Place', {
      width: 0.18,
      height: 0.022,
      fontSize: 11,
      color: '#ffaaff',
      bgColor: 'transparent',
      x: 0.095,
      y: 0.025,
    });
    if (rightDetails) {
      rightDetails.parent = this.vrUIPanel;
      this.uiElements.push(rightDetails);
    }

    const scaleHint = this.createUIText('A/B = Scale | Grip = Reset', {
      width: 0.25,
      height: 0.022,
      fontSize: 11,
      color: '#aaaaff',
      bgColor: 'transparent',
      x: 0,
      y: 0.002,
    });
    if (scaleHint) {
      scaleHint.parent = this.vrUIPanel;
      this.uiElements.push(scaleHint);
    }

    // ============================================
    // BUTTONS
    // ============================================

    // EXIT Button
    this.exitButton = this.createUIButton('EXIT', {
      width: 0.15,
      height: 0.05,
      fontSize: 24,
      color: 'white',
      bgColor: '#cc2222',
      hoverColor: '#ff4444',
      y: -0.02,
      onClick: () => this.exitVR(),
    });
    if (this.exitButton) {
      this.exitButton.parent = this.vrUIPanel;
      this.uiElements.push(this.exitButton);
    }

    // RESET Button
    const resetBtn = this.createUIButton('RESET', {
      width: 0.1,
      height: 0.04,
      fontSize: 18,
      color: 'white',
      bgColor: '#2266aa',
      hoverColor: '#3388cc',
      x: -0.12,
      y: -0.08,
      onClick: () => this.resetModelToGround(),
    });
    if (resetBtn) {
      resetBtn.parent = this.vrUIPanel;
      this.uiElements.push(resetBtn);
    }

    // DETACH/ATTACH Button
    const detachBtn = this.createUIButton('DETACH', {
      width: 0.1,
      height: 0.04,
      fontSize: 18,
      color: 'white',
      bgColor: '#226688',
      hoverColor: '#3388aa',
      x: 0.12,
      y: -0.08,
      onClick: () => this.toggleUIAttachment(),
    });
    if (detachBtn) {
      detachBtn.parent = this.vrUIPanel;
      this.uiElements.push(detachBtn);
    }

    // Tips text at bottom
    const tips = this.createUIText('Look at your left wrist to see menu', {
      width: 0.35,
      height: 0.025,
      fontSize: 12,
      color: '#888888',
      bgColor: 'transparent',
      y: -0.12,
    });
    if (tips) {
      tips.parent = this.vrUIPanel;
      this.uiElements.push(tips);
    }

    // Start hidden
    this.vrUIPanel.setEnabled(false);
  }

  /**
   * Create a text element for UI
   */
  private createUIText(text: string, options: {
    width: number;
    height: number;
    fontSize: number;
    fontWeight?: string;
    color: string;
    bgColor: string;
    x?: number;
    y?: number;
  }): BABYLON.Mesh | null {
    if (!this.scene) return null;

    const plane = BABYLON.MeshBuilder.CreatePlane(`text_${text}`, {
      width: options.width,
      height: options.height,
      sideOrientation: BABYLON.Mesh.DOUBLESIDE,
    }, this.scene);

    plane.position.x = options.x || 0;
    plane.position.y = options.y || 0;
    plane.position.z = -0.003;

    // Create texture
    const texWidth = Math.floor(options.width * 1000);
    const texHeight = Math.floor(options.height * 1000);
    const texture = new BABYLON.DynamicTexture(`tex_${text}`, {
      width: texWidth,
      height: texHeight,
    }, this.scene);

    const ctx = texture.getContext();
    ctx.clearRect(0, 0, texWidth, texHeight);

    // Background
    if (options.bgColor !== 'transparent') {
      ctx.fillStyle = options.bgColor;
      ctx.roundRect(2, 2, texWidth - 4, texHeight - 4, 6);
      ctx.fill();
    }

    // Text
    ctx.font = `${options.fontWeight || 'normal'} ${options.fontSize * 2}px Arial`;
    ctx.fillStyle = options.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, texWidth / 2, texHeight / 2);
    texture.update();

    const mat = new BABYLON.StandardMaterial(`mat_${text}`, this.scene);
    mat.diffuseTexture = texture;
    mat.emissiveTexture = texture;
    mat.useAlphaFromDiffuseTexture = true;
    mat.emissiveColor = new BABYLON.Color3(0.8, 0.8, 0.8);
    plane.material = mat;

    return plane;
  }

  /**
   * Create an interactive button for UI
   */
  private createUIButton(text: string, options: {
    width: number;
    height: number;
    fontSize: number;
    color: string;
    bgColor: string;
    hoverColor: string;
    x?: number;
    y?: number;
    onClick: () => void;
  }): BABYLON.Mesh | null {
    if (!this.scene) return null;

    const btn = BABYLON.MeshBuilder.CreatePlane(`btn_${text}`, {
      width: options.width,
      height: options.height,
      sideOrientation: BABYLON.Mesh.DOUBLESIDE,
    }, this.scene);

    btn.position.x = options.x || 0;
    btn.position.y = options.y || 0;
    btn.position.z = -0.005;
    btn.isPickable = true;

    // Create texture with gradient
    const texWidth = Math.floor(options.width * 800);
    const texHeight = Math.floor(options.height * 800);
    const texture = new BABYLON.DynamicTexture(`tex_${text}`, {
      width: texWidth,
      height: texHeight,
    }, this.scene);

    const ctx = texture.getContext();
    const gradient = ctx.createLinearGradient(0, 0, 0, texHeight);
    gradient.addColorStop(0, this.lightenColor(options.bgColor, 20));
    gradient.addColorStop(1, options.bgColor);

    ctx.fillStyle = gradient;
    ctx.roundRect(4, 4, texWidth - 8, texHeight - 8, 10);
    ctx.fill();

    // Border
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 2;
    ctx.roundRect(4, 4, texWidth - 8, texHeight - 8, 10);
    ctx.stroke();

    // Text
    ctx.font = `bold ${options.fontSize * 2}px Arial`;
    ctx.fillStyle = options.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, texWidth / 2, texHeight / 2);
    texture.update();

    const mat = new BABYLON.StandardMaterial(`mat_${text}`, this.scene);
    mat.diffuseTexture = texture;
    mat.emissiveTexture = texture;
    mat.emissiveColor = new BABYLON.Color3(0.5, 0.5, 0.5);
    btn.material = mat;

    // Store original emissive for hover effect
    const originalEmissive = mat.emissiveColor.clone();

    // Action manager for interactions
    btn.actionManager = new BABYLON.ActionManager(this.scene);

    // Hover effect
    btn.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(
        BABYLON.ActionManager.OnPointerOverTrigger,
        () => {
          mat.emissiveColor = new BABYLON.Color3(0.8, 0.8, 0.8);
        }
      )
    );

    btn.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(
        BABYLON.ActionManager.OnPointerOutTrigger,
        () => {
          mat.emissiveColor = originalEmissive;
        }
      )
    );

    // Click action
    btn.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(
        BABYLON.ActionManager.OnPickTrigger,
        () => {
          options.onClick();
        }
      )
    );

    return btn;
  }

  /**
   * Helper to lighten a hex color
   */
  private lightenColor(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, (num >> 16) + amt);
    const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
    const B = Math.min(255, (num & 0x0000ff) + amt);
    return `rgb(${R},${G},${B})`;
  }

  /**
   * Toggle UI attachment to wrist
   */
  private toggleUIAttachment(): void {
    this.uiAttachedToWrist = !this.uiAttachedToWrist;
    console.log('[VR] UI attached to wrist:', this.uiAttachedToWrist);
  }

  /**
   * Dispose all UI elements
   */
  private disposeUI(): void {
    this.uiElements.forEach(el => el.dispose());
    this.uiElements = [];
    this.exitButton?.dispose();
    this.exitButton = null;
    this.vrUIPanel?.dispose();
    this.vrUIPanel = null;
  }

  private playSpawnAnimation(targetScale: number): void {
    if (!this.model || !this.scene) return;

    const startScale = 0.01;
    this.model.scaling.setAll(startScale);

    const anim = new BABYLON.Animation(
      'spawn',
      'scaling',
      60,
      BABYLON.Animation.ANIMATIONTYPE_VECTOR3,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    const ease = new BABYLON.BackEase(0.3);
    ease.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEOUT);

    anim.setKeys([
      { frame: 0, value: new BABYLON.Vector3(startScale, startScale, startScale) },
      { frame: 30, value: new BABYLON.Vector3(targetScale, targetScale, targetScale) },
    ]);
    anim.setEasingFunction(ease);

    this.model.animations = [anim];
    this.scene.beginAnimation(this.model, 0, 30, false);
  }

  async enterVR(): Promise<void> {
    if (!this.xrHelper) {
      throw new Error('WebXR not initialized');
    }

    this._currentMode = 'vr';

    // Show environment for VR mode
    this.setEnvironmentVisible(true);

    // Restore background color
    if (this.scene && this.originalClearColor) {
      this.scene.clearColor = this.originalClearColor.clone();
    }

    await this.xrHelper.baseExperience.enterXRAsync('immersive-vr', 'local-floor');

    // Show spatial UI when entering VR
    if (this.vrUIPanel) {
      this.vrUIPanel.setEnabled(true);
    }

    // Reset joystick state
    this.leftStickX = 0;
    this.leftStickY = 0;
    this.rightStickX = 0;
    this.rightStickY = 0;
  }

  /**
   * Enter Mixed Reality (AR) mode with passthrough
   * Shows the real world as background with the 3D model overlaid
   */
  async enterAR(): Promise<void> {
    if (!this._arSupported || !this.scene) {
      throw new Error('WebXR AR not supported');
    }

    this._currentMode = 'ar';

    // Hide VR environment for AR mode (show only the model)
    this.setEnvironmentVisible(false);

    // Make background transparent for passthrough
    this.scene.clearColor = new BABYLON.Color4(0, 0, 0, 0);

    try {
      // For AR, we need to create a new XR experience or enter directly
      // The default VR experience might not support AR, so we enter AR session directly
      if (this.xrHelper) {
        await this.xrHelper.baseExperience.enterXRAsync('immersive-ar', 'local-floor');
      } else {
        // Create AR-specific experience
        const xr = await this.scene.createDefaultXRExperienceAsync({
          uiOptions: {
            sessionMode: 'immersive-ar',
            referenceSpaceType: 'local-floor',
          },
          disableDefaultUI: true,
        });

        // Add state change handler
        xr.baseExperience.onStateChangedObservable.add((state) => {
          if (state === BABYLON.WebXRState.IN_XR) {
            this._isInAR = true;
            this._isInVR = false;
            this.config.onSessionStart?.();
          } else if (state === BABYLON.WebXRState.NOT_IN_XR) {
            this._isInAR = false;
            this._currentMode = null;
            this.config.onSessionEnd?.();
          }
        });

        await xr.baseExperience.enterXRAsync('immersive-ar', 'local-floor');
      }
    } catch (error) {
      console.error('[VR Engine] Failed to enter AR:', error);
      // Restore environment if AR fails
      this.setEnvironmentVisible(true);
      if (this.originalClearColor) {
        this.scene.clearColor = this.originalClearColor.clone();
      }
      this._currentMode = null;
      throw error;
    }

    // Show spatial UI in AR mode too
    if (this.vrUIPanel) {
      this.vrUIPanel.setEnabled(true);
    }

    // Reset joystick state
    this.leftStickX = 0;
    this.leftStickY = 0;
    this.rightStickX = 0;
    this.rightStickY = 0;
  }

  /**
   * Toggle environment visibility for AR mode
   */
  private setEnvironmentVisible(visible: boolean): void {
    this.environmentMeshes.forEach(mesh => {
      mesh.isVisible = visible;
    });

    // Also hide particles in AR mode
    if (this.scene) {
      this.scene.particleSystems.forEach(ps => {
        if (visible) {
          ps.start();
        } else {
          ps.stop();
        }
      });
    }
  }

  exitVR(): void {
    // Hide spatial UI when exiting
    if (this.vrUIPanel) {
      this.vrUIPanel.setEnabled(false);
    }

    // Reset joystick state
    this.leftStickX = 0;
    this.leftStickY = 0;
    this.rightStickX = 0;
    this.rightStickY = 0;

    // Reset to VR environment
    this.setEnvironmentVisible(true);
    if (this.scene && this.originalClearColor) {
      this.scene.clearColor = this.originalClearColor.clone();
    }

    this._currentMode = null;
    this.xrHelper?.baseExperience.exitXRAsync();
  }

  // Alias for clarity
  exitAR(): void {
    this.exitVR();
  }

  resetModel(): void {
    if (this.model) {
      this.model.rotation = new BABYLON.Vector3(0, 0, 0);
      this.model.position.x = 0;
      this.model.position.z = 2;
      this.groundModel();
    }
  }

  setModelScale(scale: number): void {
    if (this.model) {
      const clampedScale = Math.max(0.1, Math.min(5, scale));
      this.model.scaling.setAll(clampedScale);
    }
  }

  setModelRotation(y: number): void {
    if (this.model) {
      this.model.rotation.y = y;
    }
  }

  setModelPosition(pos: { x: number; y: number; z: number }): void {
    if (this.model) {
      this.model.position = new BABYLON.Vector3(pos.x, pos.y, pos.z);
    }
  }

  getModelScale(): number {
    return this.model?.scaling.x ?? 1;
  }

  dispose(): void {
    this.disposeUI();
    this.placementIndicator?.dispose();
    this.leftController = null;
    this.rightController = null;
    this.xrHelper?.dispose();
    this.model?.dispose();
    this.scene?.dispose();
    this.engine?.dispose();
  }
}
